// binance wallet dashboard
import React, { useState } from "react";
import {
  useBinanceWallet,
  useBinancePositions,
  useTradeSummary,
  useRealizedPnl,
} from "./useBinanceData";
import "../user/Dashboard.css";
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import axios from "axios";
import { useContext } from "react";
import { AuthContext } from "../authContext"; // adjust path if needed


export default function WalletDashboard() {
  const { wallet, isLoading: walletLoading } = useBinanceWallet();
  const { userId } = useContext(AuthContext) || {};
  const canClose =
    String(userId || localStorage.getItem("userId")) === "pziPDtOgJiX06oGuJxNwxE";
  const { positions, isLoading: positionsLoading } = useBinancePositions();
  const { summary, isLoading: summaryLoading } = useTradeSummary();
  const { realizedPnl, isLoading: pnlLoading } = useRealizedPnl();
  const [sortField, setSortField] = useState("hour");
  const [sortDirection, setSortDirection] = useState("desc");
  const [currentPage, setCurrentPage] = useState(1);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const itemsPerPage = 20;
  const [invUsd, setInvUsd] = useState("");
  const [levX, setLevX] = useState("");
  const [calc, setCalc] = useState(null);
  const [closeModal, setCloseModal] = useState({ open: false, symbol: "", side: "", amt: null });
  const [closing, setClosing] = useState(false);
  const [tradeUsd, setTradeUsd] = useState("50");
  const isLocal = window.location.hostname === "localhost";
  const baseUrl = isLocal ? "http://localhost:5000" : "https://www.trendscout.club";
  const parseMoney = (s) => {
    const n = parseFloat(String(s ?? "").replace(/[^0-9.-]/g, ""));
    return Number.isFinite(n) ? n : null;
  };

  const parsePctAfterSlash = (s) => {
    // expects: "$7300.00 / 2.67%" OR "$7300.00 / -2.67%"
    const m = String(s ?? "").match(/\/\s*([+-]?\d+(\.\d+)?)%/);
    return m ? Number(m[1]) : null;
  };

  const isLoading =
    walletLoading || positionsLoading || summaryLoading || pnlLoading;

    if (isLoading || !wallet) {
      return (
        <div className="loading-modal">
          <div className="loading-content">
            <div className="loader" />
            <p>Loading wallet data...</p>
          </div>
        </div>
      );
    }

    function openClose(pos) {
      if (!canClose || pos.type !== "Futures") return;
      setCloseModal({
        open: true,
        symbol: String(pos.apiSymbol || pos.symbol).toUpperCase(), // e.g. ANKRUSDT
        side: String(pos.side || "").toUpperCase(),
        amt: pos.amount ?? null,
      });
    }


    function cancelClose() {
      if (closing) return; // prevent dismiss while sending
      setCloseModal({ open: false, symbol: "", side: "", amt: null });
    }

    async function confirmClose() {
      try {
        if (!canClose) {
          alert("Closing positions is restricted.");
          return;
        }
        setClosing(true);
        const token = localStorage.getItem("token");
        const clean = String(closeModal.symbol || "").toUpperCase().replace(/[^A-Z0-9]/g, "");
        await axios.post(
          `${baseUrl}/api/binance/bot/close-position`,
          { symbol: clean, side: closeModal.side },
          { headers: token ? { Authorization: `Bearer ${token}` } : {} }
        );
        setCloseModal({ open: false, symbol: "", side: "", amt: null });
        window.location.reload();
      } catch (e) {
        alert(`Close failed: ${e?.response?.data?.error || e.message}`);
      } finally {
        setClosing(false);
      }
    }


    function filterTradesByDate(history, fromDate, toDate) {
      const fromTs = fromDate ? new Date(`${fromDate}T00:00:00Z`).getTime() : null;
      const toTs   = toDate   ? new Date(`${toDate}T23:59:59.999Z`).getTime() : null;

      return (history || []).filter(trade => {
        const t = Number(trade.time);
        const ms = t < 1e12 ? t * 1000 : t; // handle seconds vs ms
        if (Number.isNaN(ms)) return false;
        if (fromTs && ms < fromTs) return false;
        if (toTs && ms > toTs) return false;
        return true;
      });
    }

    function extractUnleveredPercent(t) {
        // Prefer explicit percent fields if backend provides them.
        // Common names we might see from exchanges/ccxt:
        const cand = t.profitRate ?? t.pnlPercent ?? t.pnlRate ?? t.rate ?? t.roi ?? null;

        if (cand != null && !Number.isNaN(Number(cand))) {
          const n = Number(cand);
          // Heuristic: if in [ -1 .. 1 ] assume it's a fraction → convert to %
          return Math.abs(n) <= 1 ? n * 100 : n;
        }

        // Derive from income / notional if we have enough fields.
        // Try a few likely shapes:
        const income = Number(t.income);
        if (!Number.isFinite(income)) return null;

        // Possible notional/margin/size combos:
        const qty      = Number(t.qty ?? t.amount ?? t.size ?? t.vol ?? t.volume);
        const avgPrice = Number(t.avgPrice ?? t.price ?? t.fillPrice ?? t.execPrice ?? t.entryPrice);
        const notional = Number(t.notional ?? t.value ?? (Number.isFinite(qty) && Number.isFinite(avgPrice) ? qty * avgPrice : NaN));
        const margin   = Number(t.margin ?? t.marginAmount);

        // Prefer true notional (qty*price). If missing, fall back to margin.
        let base = Number.isFinite(notional) && notional > 0 ? notional
                : (Number.isFinite(margin)   && margin   > 0 ? margin : NaN);

        if (!Number.isFinite(base) || base <= 0) return null;

        // % = income / base * 100 (unlevered if base is actual notional at entry)
        return (income / base) * 100;
      }


    function handleCalculate() {
      const filtered = filterTradesByDate(realizedPnl?.history || [], fromDate, toDate);

      // Gather percent for each trade (skip ones we can't resolve)
      const percents = filtered
        .map(extractUnleveredPercent)
        .filter(p => Number.isFinite(p));

      // If we couldn't resolve per-trade %, gracefully fall back to your old baseline
      // so the button never feels "broken".
      const fallbackTotalIncome = filtered.reduce((s, t) => s + Number(t?.income || 0), 0);
      const hasAnyPct = percents.length > 0;

      // Sum of simple percents (no compounding, per your instruction "do nothing else")
      const sumPct = hasAnyPct ? percents.reduce((a, b) => a + b, 0) : null;

      const investment = Number(invUsd) || 0;
      const leverage   = Number(levX)   || 1;

      let scaledPnl, mode;
      if (hasAnyPct) {
        // Percent mode
        scaledPnl = investment * leverage * (sumPct / 100);
        mode = "percent";
      } else {
        // Fallback mode: legacy baseline of 1000 (keeps old behavior alive)
        const BASE = 1000;
        scaledPnl = investment * leverage * (fallbackTotalIncome / BASE);
        mode = "fallback-1000";
      }

      setCalc({
        tradeCount: filtered.length,
        usedPercents: percents.length,
        sumPct: hasAnyPct ? sumPct : null,
        totalIncome: fallbackTotalIncome, // for reference
        investment,
        leverage,
        mode,
        scaledPnl
      });
    }

  function fmtSign(n, digits = 2) {
  if (!Number.isFinite(n)) return "—";
  const s = n.toFixed(digits);
  return (n > 0 ? "+" : "") + s;
}

function calcProjected(pos) {
  const { isLong, amount, entryPrice, markPrice, tp, sl } = pos;
  if (!Number.isFinite(amount) || amount <= 0 || !Number.isFinite(entryPrice) || entryPrice <= 0) {
    return {
      pctNow: null, usdNow: null,
      pctToTp: null, usdAtTp: null,
      pctToSl: null, usdAtSl: null,
      rr: null,
      progToTp: null, progToSl: null,
    };
  }

  // % change from entry to X (unlevered)
  const pctTo = (target) => {
    if (!Number.isFinite(target) || target <= 0) return null;
    const raw = isLong ? (target - entryPrice) / entryPrice : (entryPrice - target) / entryPrice;
    return raw * 100;
  };

  // USDT PnL from entry to X (qty in base * price delta; leverage doesn’t change USDT PnL)
  const usdTo = (target) => {
    if (!Number.isFinite(target)) return null;
    const delta = (target - entryPrice) * amount * (isLong ? 1 : -1);
    return delta;
  };

  const pctNow = pctTo(markPrice);
  const usdNow = usdTo(markPrice);

  const pctToTp = pctTo(tp);
  const usdAtTp = usdTo(tp);

  const pctToSl = pctTo(sl);
  const usdAtSl = usdTo(sl);

  // R:R = |reward| / |risk|
  let rr = null;
  if (Number.isFinite(usdAtTp) && Number.isFinite(usdAtSl)) {
    const reward = Math.abs(usdAtTp);
    const risk   = Math.abs(usdAtSl);
    rr = risk > 0 ? reward / risk : null;
  }

  // Progress (0..1) from entry → target, clamped
  const clamp01 = (x) => Math.max(0, Math.min(1, x));
  let progToTp = null, progToSl = null;

  if (Number.isFinite(tp)) {
    const num = isLong ? (markPrice - entryPrice) : (entryPrice - markPrice);
    const den = isLong ? (tp - entryPrice)       : (entryPrice - tp);
    progToTp = Number.isFinite(den) && den > 0 ? clamp01(num / den) : null;
  }
  if (Number.isFinite(sl)) {
    const num = isLong ? (entryPrice - markPrice) : (markPrice - entryPrice);
    const den = isLong ? (entryPrice - sl)        : (sl - entryPrice);
    progToSl = Number.isFinite(den) && den > 0 ? clamp01(num / den) : null;
  }

  return { pctNow, usdNow, pctToTp, usdAtTp, pctToSl, usdAtSl, rr, progToTp, progToSl };
}



  // Spot Holdings
  const spotHoldings = Object.entries(wallet.spot)
    .map(([symbol, value]) => {
      const amount = parseFloat(value.available);
      const price =
        symbol === "USDT" || symbol === "BUSD" || symbol === "FDUSD"
          ? 1
          : wallet.prices?.[`${symbol}USDT`] || 0;
      const total = amount * price;

      return {
        symbol,
        type: "Spot",
        amount,
        entryPrice: null,
        markPrice: price,
        pnl: null,
        total,
      };
    })
    .filter(({ total }) => total > 10);

  const spotTotalValue = spotHoldings.reduce((acc, cur) => acc + cur.total, 0);

  const futuresHoldings = (positions || [])
    .filter(p => parseFloat(p.positionAmt) !== 0)
    .map(p => {
      const rawAmt     = parseFloat(p.positionAmt);
      const amount     = Math.abs(rawAmt);
      const entryPrice = parseFloat(p.entryPrice);
      const markPrice  = parseFloat(p.markPrice);
      const pnl        = parseFloat(p.unRealizedProfit || p.unrealizedProfit || "0");

      const side = (p.positionSide && p.positionSide !== 'BOTH')
        ? p.positionSide.toUpperCase()
        : (rawAmt >= 0 ? 'LONG' : 'SHORT');

      const total = isNaN(amount * markPrice) ? 0 : amount * markPrice;
      const baseNotionalAtEntry = Math.abs(entryPrice * rawAmt);
      const percentPnl =
        entryPrice !== 0 && !isNaN(entryPrice) && !isNaN(rawAmt)
          ? (pnl / (baseNotionalAtEntry || Math.abs(entryPrice * amount))) * 100
          : 0;

      // NEW: pull tp/sl from backend (with a few aliases to be safe)
      const tp = Number(p.takeProfit ?? p.tp ?? p.tpPrice ?? NaN);
      const sl = Number(p.stopLoss   ?? p.sl ?? p.slPrice ?? NaN);

      return {
        symbol: p.symbol.replace("USDT", ""),
        apiSymbol: p.symbol,    
        type: "Futures",
        side,
        isLong: side === 'LONG',
        amount,
        entryPrice: isNaN(entryPrice) ? 0 : entryPrice,
        markPrice: isNaN(markPrice) ? 0 : markPrice,
        pnl,
        percentPnl,
        total,
        tp: Number.isFinite(tp) ? tp : null,
        sl: Number.isFinite(sl) ? sl : null,
      };
    });


    // After building futuresHoldings
  const totalUnrealizedPnl = futuresHoldings.reduce((s, p) => s + (Number(p.pnl) || 0), 0);

  const totalEntryNotional = futuresHoldings.reduce((s, p) => {
    const entry = Number(p.entryPrice) || 0;
    const amt   = Math.abs(Number(p.amount) || 0);
    return s + (entry * amt);
  }, 0);

  const runningPnlPct = totalEntryNotional > 0
    ? (totalUnrealizedPnl / totalEntryNotional) * 100
    : 0;


  const allPositions = [...spotHoldings, ...futuresHoldings];

  // Group closed trades and compute custom TP/SL
  let groupedTrades = {};
  let customTp = 0;
  let customSl = 0;

  if (realizedPnl && realizedPnl.history) {
const filteredHistory = realizedPnl.history.filter((trade) => {
  const tradeDate = new Date(trade.time);
  const from = fromDate ? new Date(fromDate) : null;
  const to = toDate ? new Date(toDate) : null;

  if (to) to.setHours(23, 59, 59, 999); // include full day

  return (!from || tradeDate >= from) && (!to || tradeDate <= to);
});

groupedTrades = filteredHistory.reduce((acc, trade) => {
  const symbol = trade.symbol;
  const date = new Date(trade.time);
  const hourKey = `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()} ${date.getHours()}:00`;

  const key = `${symbol}_${hourKey}`;
  if (!acc[key]) {
    acc[key] = {
      symbol,
      hour: hourKey,
      income: 0,
      count: 0,
    };
  }

  acc[key].income += parseFloat(trade.income);
  acc[key].count += 1;
  return acc;
}, {});

// 💡 Place this right after:
customTp = 0;
customSl = 0;

Object.values(groupedTrades).forEach((group) => {
  if (group.income > 0) customTp++;
  else customSl++;
});

  }

  // Sort + paginate groupedTrades:
  const groupedTradeList = Object.values(groupedTrades);

  const sortedTrades = groupedTradeList.sort((a, b) => {
    let valA = a[sortField];
    let valB = b[sortField];

    // Convert date string to timestamp for sorting by hour
    if (sortField === "hour") {
      valA = new Date(valA).getTime();
      valB = new Date(valB).getTime();
    }

    if (valA < valB) return sortDirection === "asc" ? -1 : 1;
    if (valA > valB) return sortDirection === "asc" ? 1 : -1;
    return 0;
  });

  const paginatedTrades = sortedTrades.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // Add this helper to toggle sorting:
  function handleSort(field) {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  }

  const handleExportPdf = () => {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'a4' });

  // ✅ TrendScout tech theme: charcoal + cyan, print-friendly cards
  const PDF_THEME = {
    // Dark UI vibe (used for headers only)
    charcoal: [14, 18, 28],        // deep charcoal (matches your UI)
    charcoal2: [18, 24, 38],       // slightly lighter for accents
    white: [255, 255, 255],  // header text
    cyanSoft: [210, 248, 255],     // subtle cyan wash

    // Card surfaces (light, but cool/tech)
    card: [246, 249, 255],         // cool slate
    cardAlt: [238, 244, 255],      // stripe
    keyRow: [228, 238, 255],       // key highlight row
    line: [190, 205, 230],         // soft grid line (techy, not black)

    // Text
    ink: [16, 22, 34],             // primary text (deep slate)
    label: [88, 102, 128],         // labels muted
    value: [20, 28, 44],           // values darker
    muted: [120, 135, 160], 
    // PnL
    green: [0, 170, 120],
    red: [220, 60, 85],
  };

  const TABLE_BASE = {
    theme: "grid",
    styles: {
      font: "helvetica",
      fontSize: 10,
      cellPadding: 8,
      lineColor: PDF_THEME.line,
      lineWidth: 0.7,
      valign: "middle",
    },
    headStyles: {
      fillColor: PDF_THEME.charcoal,
      textColor: PDF_THEME.white,
      fontStyle: "bold",
      fontSize: 11,
      halign: "center",
      cellPadding: 9,
    },
    bodyStyles: {
      fillColor: PDF_THEME.card,
      textColor: PDF_THEME.ink,
      halign: "center",
    },
    alternateRowStyles: {
      fillColor: PDF_THEME.cardAlt,
    },
    margin: { left: 40, right: 40 },
  };


      // FILTER closed trades based on from/to dates
    const from = fromDate ? new Date(fromDate) : null;
    const to = toDate ? new Date(toDate + 'T23:59:59') : null;

    const filteredHistory = (realizedPnl?.history || []).filter(trade => {
      const tradeDate = new Date(trade.time);
      return (!from || tradeDate >= from) && (!to || tradeDate <= to);
    });

    const filteredGroupedTrades = filteredHistory.reduce((acc, trade) => {
      const symbol = trade.symbol;
      const date = new Date(trade.time);
      const hourKey = `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()} ${date.getHours()}:00`;
      const key = `${symbol}_${hourKey}`;

      if (!acc[key]) {
        acc[key] = {
          symbol,
          hour: hourKey,
          income: 0,
          count: 0,
        };
      }

      acc[key].income += parseFloat(trade.income);
      acc[key].count += 1;
      return acc;
    }, {});


      doc.setCharSpace(0.5);
      doc.setFontSize(18);
      doc.setTextColor(...PDF_THEME.white);

      // charcoal pill header
      doc.setFillColor(...PDF_THEME.charcoal);
      doc.rect(40, 38, 515, 36, "F");

      doc.text("CRG Trading Summary Report", 55, 63);

      // Spot/Futures Summary
      const totalValue = (
        parseFloat(wallet.futures.totalWalletBalance) + spotTotalValue
      ).toFixed(2);

    // compute baseline PnL dynamically
    const baseCapital = Number(wallet.crgTradingAmount || 0);
    const currentEquity =
      Number(wallet.futures?.totalWalletBalance || 0);
    const realizedPnlValue = baseCapital ? currentEquity - baseCapital : 0;

    const summaryBody = [
      ['Total Funds', `$${totalValue}`],
      ['Spot', `$${spotTotalValue.toFixed(2)}`],
      ['Futures', `$${parseFloat(wallet.futures.totalWalletBalance).toFixed(2)}`],
      [
        'Realized PNL',
        `$${realizedPnlValue.toFixed(2)}${
          realizedPnlValue >= 0 ? ' (Gain)' : ' (Loss)'
        }`,
      ],
      ['Win Rate', `${((customTp / (customTp + customSl)) * 100).toFixed(2)}%`],
    ];



      let filteredWins = 0;
    let filteredLosses = 0;
    let filteredTotalPnl = 0;

    Object.values(filteredGroupedTrades).forEach(group => {
      filteredTotalPnl += group.income;
      if (group.income > 0) filteredWins++;
      else filteredLosses++;
    });

    const filteredTradeCount = filteredWins + filteredLosses;
    const filteredWinRate = filteredTradeCount > 0
      ? ((filteredWins / filteredTradeCount) * 100).toFixed(2)
      : '0.00';

    // ✅ NEW: trade-size based ROI calcs
    const perTradeUsd = Math.max(0, Number(tradeUsd) || 0);

    // A) simple capital = $perTrade * trades
    const totalUsedA = perTradeUsd * filteredTradeCount;
    const roiA = totalUsedA > 0 ? (filteredTotalPnl / totalUsedA) * 100 : 0;

    // B) effective capital with recycling every 20 hours
    const AVG_OUTCOME_HRS = 20;

    // window duration in hours (based on filter)
    let windowHours = 0;
    if (from && to) {
      windowHours = Math.max(0, (to.getTime() - from.getTime()) / (1000 * 60 * 60));
    } else {
      // fallback: estimate based on first/last trade in filteredHistory
      const times = filteredHistory.map(t => new Date(t.time).getTime()).filter(Number.isFinite).sort((a,b) => a-b);
      if (times.length >= 2) windowHours = Math.max(0, (times[times.length - 1] - times[0]) / (1000 * 60 * 60));
    }

    // how many "cycles" of capital fit into the window
    const cycles = Math.max(1, Math.ceil(windowHours / AVG_OUTCOME_HRS));

    // effective capital needed if you can re-use funds every 20h:
    // spread trades across cycles => peak concurrent trades ≈ ceil(tradeCount / cycles)
    const peakConcurrentTrades = Math.max(1, Math.ceil(filteredTradeCount / cycles));
    const effectiveUsedB = perTradeUsd * peakConcurrentTrades;
    const roiB = effectiveUsedB > 0 ? (filteredTotalPnl / effectiveUsedB) * 100 : 0;

    autoTable(doc, {
      ...TABLE_BASE,
      startY: 90,
      head: [["Total Data", "Value"]],
      body: summaryBody,

    didParseCell(data) {
      if (data.section !== "body") return;

      const label = String(data.row?.raw?.[0] || "");

      // labels column
      if (data.column.index === 0) {
        data.cell.styles.textColor = PDF_THEME.label;
        return;
      }

      // values column -> mono font for tech vibe
      if (data.column.index === 1) {
        data.cell.styles.font = "courier";
        data.cell.styles.textColor = PDF_THEME.value;

        if (label === "Realized PNL") {
          const v = parseMoney(String(data.cell?.raw || ""));
          if (v != null) {
            data.cell.styles.textColor = v < 0 ? PDF_THEME.red : PDF_THEME.green;
            data.cell.styles.fontStyle = "bold";
          }
        }
      }
    }

    });


    autoTable(doc, {
      ...TABLE_BASE,
      startY: doc.lastAutoTable.finalY + 16,
      head: [["Filtered Data", "Value"]],
      body: [
        ["Filtered PNL", `$${filteredTotalPnl.toFixed(2)}`],
        ["Filtered Win Rate", `${filteredWinRate}%`],
        ["Amount/Trade", `$${perTradeUsd.toFixed(0)} / ${filteredTradeCount} trades`],
        ["Capital Used (per-trade)", `$${totalUsedA.toFixed(2)} / ${roiA.toFixed(2)}%`],
        ["Filter Window", `${windowHours.toFixed(1)} hrs (~${(windowHours / 24).toFixed(2)} days)`],
        ["Capital Used (recycled)", `$${effectiveUsedB.toFixed(2)} / ${roiB.toFixed(2)}%`],
      ],

      didParseCell(data) {
        if (data.section !== "body") return;

        const label = String(data.row?.raw?.[0] || "");
        const value = String(data.cell?.raw || "");

        const key = new Set([
          "Filtered PNL",
          "Capital Used (per-trade)",
          "Capital Used (recycled)",
        ]);

        // ✅ subtle key-row highlight (both columns)
        if (key.has(label)) {
          data.cell.styles.fillColor = PDF_THEME.keyRow;
        }

        // labels column
        if (data.column.index === 0) {
          data.cell.styles.textColor = PDF_THEME.label;
          if (key.has(label)) data.cell.styles.fontStyle = "bold";
          return;
        }

        // values column (mono font)
        if (data.column.index === 1) {
          data.cell.styles.font = "courier";
          data.cell.styles.textColor = PDF_THEME.value;

          // Filtered PNL green/red
          if (label === "Filtered PNL") {
            const v = parseMoney(value);
            if (v != null) {
              data.cell.styles.textColor = v < 0 ? PDF_THEME.red : PDF_THEME.green;
              data.cell.styles.fontStyle = "bold";
            }
            return;
          }

          // Capital Used rows color by return % after slash
          if (label === "Capital Used (per-trade)" || label === "Capital Used (recycled)") {
            const pct = parsePctAfterSlash(value);
            if (pct != null) {
              data.cell.styles.textColor = pct < 0 ? PDF_THEME.red : PDF_THEME.green;
              data.cell.styles.fontStyle = "bold";
            }
          }
        }
      }

    });


    autoTable(doc, {
      ...TABLE_BASE,
      startY: doc.lastAutoTable.finalY + 26,
      head: [["Open", "Type", "Amount", "Entry", "Mark/Spot", "PNL", "Value"]],
      body: allPositions.map(pos => [
        pos.symbol,
        pos.type,
        pos.amount.toFixed(0),
        pos.entryPrice !== null ? `$${pos.entryPrice.toFixed(4)}` : "-",
        pos.markPrice !== null ? `$${pos.markPrice.toFixed(4)}` : "-",
        typeof pos.pnl === "number"
          ? `$${pos.pnl.toFixed(2)} (${pos.percentPnl?.toFixed(2)}%)`
          : "-",
        pos.total !== null ? `$${pos.total.toFixed(2)}` : "-",
      ]),

      didParseCell(data) {
        // color PNL column (index 5)
        if (data.section === "body" && data.column.index === 5) {
          const v = parseMoney(data.cell.raw);
          if (v != null) {
            data.cell.styles.textColor = v < 0 ? PDF_THEME.red : PDF_THEME.green;
            data.cell.styles.fontStyle = "bold";
          }
        }
      },
    });


      // ✅ Make an ordered array (so numbering matches order)
      const closedRows = Object.values(filteredGroupedTrades).sort((a, b) => {
        // hour format: "YYYY-M-D H:00" → Date() can be flaky, so parse manually
        const toMs = (h) => {
          // split "YYYY-M-D H:00"
          const [ymd, hm] = String(h || "").split(" ");
          const [Y, M, D] = (ymd || "").split("-").map(Number);
          const H = Number(String(hm || "0").split(":")[0]);
          return Date.UTC(Y || 1970, (M || 1) - 1, D || 1, H || 0, 0, 0);
        };
        return toMs(b.hour) - toMs(a.hour); // newest first
      });

        autoTable(doc, {
          ...TABLE_BASE,
          startY: doc.lastAutoTable.finalY + 26,
          head: [["#", "Closed", "Hour", "Income (USDT)", "Income %"]],
          body: closedRows.map((t, i) => [
            i + 1,
            t.symbol,
            t.hour,
            `$${t.income.toFixed(2)}`,
            `${((t.income / 1000) * 100).toFixed(2)}%`,
          ]),

          didParseCell(data) {
            // Income column index 3
            if (data.section === "body" && data.column.index === 3) {
              const v = parseMoney(data.cell.raw);
              if (v != null) {
                data.cell.styles.textColor = v < 0 ? PDF_THEME.red : PDF_THEME.green;
                data.cell.styles.fontStyle = "bold";
              }
            }
          },
        });


        // ✅ footer with page numbers + generated date
        const pageCount = doc.getNumberOfPages();
        for (let i = 1; i <= pageCount; i++) {
          doc.setPage(i);
          doc.setFont("helvetica", "normal");
          doc.setFontSize(9);
          doc.setTextColor(...PDF_THEME.muted);

          const footerY = doc.internal.pageSize.getHeight() - 18;
          doc.text(
            `Generated: ${new Date().toLocaleString()}`,
            40,
            footerY
          );

          const rightX = doc.internal.pageSize.getWidth() - 40;
          doc.text(`Page ${i} / ${pageCount}`, rightX, footerY, { align: "right" });
        }

      doc.save(`CRG_Trading_Report_${new Date().toISOString().split('T')[0]}.pdf`);
    };


  return (
    <div className="dashboard-main">
      <div className="outcome-table-wrapper-dashboard">
        <div className="outcome-table">
          {/* Summary Cards */}
          <div className="expanded-content">
            <div className="expanded-section">
              <h3>💸 Funds</h3>
              <p>
                <p>
                  <strong>Total:</strong>{" "}
                  ${(
                    parseFloat(wallet.futures.totalWalletBalance) +
                    spotTotalValue
                  ).toFixed(0)}{" "}
                </p>
              </p>
              <p>
                <strong>Spot:</strong> ${spotTotalValue.toFixed(0)}
              </p>
              <p>
                <strong>Futures:</strong>{" "}
                ${parseFloat(wallet.futures.totalWalletBalance).toFixed(0)}
              </p>
              {(() => {
                const totalEquity =
                  parseFloat(wallet.futures?.totalWalletBalance || 0) +
                  parseFloat(wallet.spotTotalValue || 0 || 0);

                const baseCapital = Number(wallet.crgTradingAmount || 0);
                const pnl = baseCapital ? totalEquity - baseCapital : 0;

                return (
                  <p>
                    <strong>Realized PNL:</strong>{" "}
                    <span style={{ color: pnl >= 0 ? "lime" : "red", fontWeight: "bold" }}>
                      ${pnl.toFixed(0)}
                    </span>
                  </p>
                );
              })()}
            </div>

            <div className="expanded-section">
              <h3>📊 Performance Summary</h3>
              <p><strong>Total Trades:</strong> {customTp + customSl} <strong>Win Rate:</strong> {((customTp / (customTp + customSl)) * 100).toFixed(2)}%</p>
              <p><strong>Wins:</strong> {customTp} <strong>Losses:</strong> {customSl}</p>

              {/* 👇 Running (unrealized) PnL */}
              <p>
                <strong>Running PnL:</strong>{" "}
                <span style={{ color: totalUnrealizedPnl >= 0 ? "lime" : "red", fontWeight: "bold" }}>
                  ${totalUnrealizedPnl.toFixed(0)} ({runningPnlPct.toFixed(2)}%)
                </span>
              </p>
              <label style={{ display: "block", marginTop: 10, fontSize: 13, opacity: 0.9 }}>
                Amount per trade ($):
              </label>
              <input
                className="profit-input"
                type="number"
                min="0"
                step="1"
                placeholder="e.g. 50"
                value={tradeUsd}
                onChange={(e) => setTradeUsd(e.target.value)}
              />
              <button onClick={handleExportPdf} className="trade-export-button" style={{ marginTop: '15px' }}>
                📄 Export to PDF
              </button>
            </div>

                         <div className="expanded-section">
              <h3>💸 Filter</h3>
                <div>
                  <label>From:</label>
                  <input className="date-input-wallet"
                    type="date"
                    value={fromDate}
                    onChange={(e) => setFromDate(e.target.value)}
                  />
                </div>
                <div>
                  <label>To:</label>
                   <input className="date-input-wallet"
                    type="date"
                    value={toDate}
                    onChange={(e) => setToDate(e.target.value)}
                  />
                </div>
            </div>
                        <div className="expanded-section">
                <div className="profit-card-header">
                  <span role="img" aria-label="chart">📈</span>&nbsp; Profit Calculator
                </div>

                <label className="profit-label">Investment ($):</label>
                <input
                  className="profit-input"
                  type="number"
                  placeholder="e.g. 1000"
                  value={invUsd}
                  onChange={(e) => setInvUsd(e.target.value)}
                />

                <label className="profit-label">Leverage (x):</label>
                <input
                  className="profit-input"
                  type="number"
                  placeholder="e.g. 3"
                  value={levX}
                  onChange={(e) => setLevX(e.target.value)}
                />

                <button className="profit-btn" style={{ marginTop: '15px' }} onClick={handleCalculate}>Calculate</button>

                {calc && (
                  <div className="profit-result">
                    <div><strong>Closed trades used:</strong> {calc.tradeCount}</div>
                    <div>
                      <strong>Hypothetical PnL:</strong>{" "}
                      <span style={{ color: calc.scaledPnl >= 0 ? "lime" : "red", fontWeight: 700 }}>
                        ${calc.scaledPnl.toFixed(2)}
                      </span>
                    </div>
                  </div>
                )}
              </div>
          </div>

          <div className="outcome-table">
            <div className="table-scroll-container">
              <table>
                <thead>
                  <tr>
                    <th>Holdings</th>
                    <th className="hide">Type</th>
                    <th>Side</th>
                    <th>Value</th>
                    <th>Entry Price</th>
                    <th>Mark Price</th>
                    <th>TP</th>      
                    <th>SL</th> 
                    <th>Running PNL</th>
                    <th>→ TP PnL</th>
                    <th>→ SL PnL</th>
                    <th>R:R</th>
                  </tr>
                </thead>
                  <tbody>
                  {allPositions.map((pos, idx) => {
                    const proj = pos.type === "Futures" ? calcProjected(pos) : {};
                    return (
                      <tr
                       key={idx}
                        onClick={canClose && pos.type === "Futures" ? () => openClose(pos) : undefined}
                        style={{
                          cursor: canClose && pos.type === "Futures" ? "pointer" : "default",
                          opacity: !canClose && pos.type === "Futures" ? 0.7 : 1
                        }}
                        title={canClose && pos.type === "Futures" ? "Click to close position" : ""}
                      >
                        <td>{pos.symbol}</td>
                        <td className="hide">{pos.type}</td>
                        <td>
                          {pos.type === "Futures" ? (
                            <span
                              className="side-badge"
                              style={{
                                background: pos.isLong ? "rgba(0,255,0,0.12)" : "rgba(255,0,0,0.12)",
                                color: pos.isLong ? "lime" : "red",
                                border: `1px solid ${pos.isLong ? "rgba(0,255,0,0.35)" : "rgba(255,0,0,0.35)"}`
                              }}
                            >
                              {pos.side}
                            </span>
                          ) : "—"}
                        </td>
                      <td>${pos.total != null ? pos.total.toFixed(0) : '-'}</td>
                      <td>${pos.entryPrice != null ? pos.entryPrice.toFixed(4) : '-'}</td>
                      <td>${pos.markPrice != null ? pos.markPrice.toFixed(4) : '-'}</td>
                      <td>{pos.tp != null ? pos.tp.toFixed(4) : '—'}</td>
                      <td>{pos.sl != null ? pos.sl.toFixed(4) : '—'}</td>
                      <td style={{ color: pos.pnl > 0 ? 'lime' : pos.pnl < 0 ? 'red' : 'white', whiteSpace: 'nowrap' }}>
                        {typeof pos.pnl === 'number' ? `$${pos.pnl.toFixed(0)}` : '-'} (
                        {typeof pos.percentPnl === 'number' ? `${pos.percentPnl.toFixed(2)}%` : '-'}
                        )
                      </td>
                            {/* → TP */}
                      <td style={{ whiteSpace: 'nowrap', color: proj?.usdAtTp > 0 ? "lime" : proj?.usdAtTp < 0 ? "red" : "white" }}>
                        {Number.isFinite(proj?.usdAtTp) ? `$${fmtSign(proj.usdAtTp, 0)} (${fmtSign(proj.pctToTp, 2)}%)` : '—'}
                        {Number.isFinite(proj?.progToTp) && (
                          <div style={{ width: 80, height: 6, background: "rgba(255,255,255,0.1)", borderRadius: 6, overflow: "hidden", display: "inline-block", marginLeft: 6, verticalAlign: "middle" }}>
                            <div style={{ width: `${(proj.progToTp * 100).toFixed(0)}%`, height: "100%", background: "rgba(0,255,0,0.5)" }} />
                          </div>
                        )}
                      </td>

                      {/* → SL */}
                      <td style={{ whiteSpace: 'nowrap', color: proj?.usdAtSl > 0 ? "lime" : proj?.usdAtSl < 0 ? "red" : "white" }}>
                        {Number.isFinite(proj?.usdAtSl) ? `$${fmtSign(proj.usdAtSl, 0)} (${fmtSign(proj.pctToSl, 2)}%)` : '—'}
                        {Number.isFinite(proj?.progToSl) && (
                          <div style={{ width: 80, height: 6, background: "rgba(255,255,255,0.1)", borderRadius: 6, overflow: "hidden", display: "inline-block", marginLeft: 6, verticalAlign: "middle" }}>
                            <div style={{ width: `${(proj.progToSl * 100).toFixed(0)}%`, height: "100%", background: "rgba(255,0,0,0.5)" }} />
                          </div>
                        )}
                      </td>

                      {/* R:R */}
                      <td style={{ fontWeight: 700 }}>
                        {Number.isFinite(proj?.rr) ? proj.rr.toFixed(2) : "—"}
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {realizedPnl && realizedPnl.history && (
            <div className="outcome-table">
                    {fromDate && toDate && (
                  <p style={{ marginBottom: "10px" }}>
                    Showing trades from <strong>{fromDate}</strong> to <strong>{toDate}</strong>
                  </p>
                )}
              <div className="table-scroll-container">
                <table>
                  <thead>
                    <tr>
                      <th onClick={() => handleSort("symbol")}>Closed</th>
                      <th onClick={() => handleSort("hour")}>Hour</th>
                      <th onClick={() => handleSort("income")}>
                        PnL
                      </th>
                      <th onClick={() => handleSort("count")}>PnL</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedTrades.map((group, idx) => (
                      <tr key={idx}>
                        <td>{group.symbol}</td>
                        <td>{group.hour}</td>
                        <td
                          style={{ color: group.income >= 0 ? "lime" : "red", fontWeight: "bold" }}
                        >
                          ${group.income.toFixed(0)}
                        </td>
                        <td>{(group.income / 1000 * 100).toFixed(2)}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {/* Pagination Controls */}
                <div className="wallet-table-center">
                  <div
                    style={{ marginTop: "10px", display: "flex", gap: "10px" }}
                  >
                    <button
                      disabled={currentPage === 1}
                      onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                    >
                      Prev
                    </button>
                    <span>
                      Page {currentPage} /{" "}
                      {Math.ceil(groupedTradeList.length / itemsPerPage)}
                    </span>
                    <button
                      disabled={
                        currentPage ===
                        Math.ceil(groupedTradeList.length / itemsPerPage)
                      }
                      onClick={() => setCurrentPage((p) => p + 1)}
                    >
                      Next
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      {closeModal.open && (
        <div className="modal-overlay" onClick={cancelClose}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h3>Close Position</h3>
            <p>
              Are you sure you want to close <strong>{closeModal.side}</strong> on{" "}
              <strong>{closeModal.symbol}</strong>
              {closeModal.amt != null ? <> (size ≈ {closeModal.amt})</> : null}?
            </p>
            <div className="modal-actions">
              <button onClick={cancelClose} disabled={closing}>Cancel</button>
              <button className="danger" onClick={confirmClose} disabled={closing || !canClose}>
                {closing ? "Closing…" : "CLOSE POSITION"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
